import { useAuthStore } from '@/stores';
import { isTokenExpired } from '@/helpers/token';

// Returned instead of a rejection once the session is over: logout() is
// already taking the user to the login page, so every caller's catch() would
// only print "Failed to load ...: 401" into the console for a page that is
// going away (the errors seen 2026-10-11 after a token expired).
const SESSION_ENDED = () => new Promise(() => {});

const VITE_API_URL = process.env.VUE_APP_API_URL || `http://localhost:5000/`;

export const fetchWrapper = {
    get: request('GET'),
    post: request('POST'),
    put: request('PUT'),
    delete: request('DELETE'),
    'url': VITE_API_URL,
};

function request(method) {   
    return (url, body) => {
        const auth = useAuthStore();
        if (auth.user && isTokenExpired(auth.user.token)) {
            auth.logout();
            return SESSION_ENDED();
        }

        var spinner = document.getElementById("spinner");
        spinner?.classList.add("show");

        const isApiUrl = url.startsWith(VITE_API_URL);
        if (!isApiUrl) {
            url = VITE_API_URL + url;
        }

        const requestOptions = {
            method,
            headers: authHeader(url),

            // ❌ DO NOT ADD credentials here
            // credentials: 'include'  <-- REMOVE if added anywhere
        };

        if (body instanceof FormData) {
            requestOptions.body = body;
        } else if (body) {
            requestOptions.headers['Content-Type'] = 'application/json';
            requestOptions.body = JSON.stringify(body);
        }

        return fetch(url, requestOptions).then(handleResponse);
    }
}

// =========================
// AUTH HEADER (CORRECT)
// =========================
// fetchWrapper — match what your middleware expects
function authHeader(url) {
  const { user } = useAuthStore();
  const isLoggedIn = !!user?.token;
  const isApiUrl = url.startsWith(VITE_API_URL);

  if (isLoggedIn && isApiUrl) {
    return {
      Authorization: `Bearer ${user.token}`, // ✅ consistent with GraphQL
    };
  }
  return {};
}

async function handleResponse(response) {
    const isJson = response.headers?.get('content-type')?.includes('application/json');
    const data = isJson ? await response.json() : null;
    var spinner = document.getElementById("spinner");
    if (!response.ok) {
        const { user, logout } = useAuthStore();

        if ([401, 403].includes(response.status) && user) {
            spinner?.classList.remove("show");
            logout();
            return SESSION_ENDED();
        }

        const error = (data && data.message) || response.status;

        spinner?.classList.remove("show");

        return Promise.reject(error);
    }

    spinner?.classList.remove("show");

    return data;
}
// fetchWrapper rejects with the server's message string, or the bare HTTP
// status number when there was no JSON message -- never an Error object.
export function describeError(error, fallback = 'Request failed.') {
    if (typeof error === 'string' && error) return error;
    if (typeof error === 'number') return `${fallback} (HTTP ${error})`;
    if (error?.message) return error.message;
    return fallback;
}
